import React, { useState, useEffect, useRef } from "react";
import {
  View,
  Text,
  SectionList,
  StyleSheet,
  Pressable,
  Platform,
} from "react-native";
import { MainNavProps } from "../navigation/main/MainParamList";
import {
  ColorLine,
  black,
  black90,
  color_333333,
  color_999999,
  color_main,
  white,
  ColorF5F5F5,
} from "../../assets/style/colors";
import {
  Str_AccordingToDateFind,
  Str_Friday,
  Str_Monday,
  Str_Saturday,
  Str_Sunday,
  Str_Thursday,
  Str_Tuesday,
  Str_Wednesday,
} from "../../language/string_res";
import { ScreenCloseModal, ScreenModal } from "../../components/ScreenModal";
import { MessageType } from "../../constants/types";

type DateItem = {
  date: Date;
  isEmpty: boolean;
};

type Section = {
  title: string;
  data: DateItem[][];
};

const today = new Date();
const MAX_DATE = new Date(
  today.getFullYear(),
  today.getMonth(),
  today.getDate()
);

const generateDatesForMonth = (year: number, month: number): DateItem[][] => {
  const dates: DateItem[] = [];
  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const startDayOfWeek = (firstDayOfMonth.getDay() + 6) % 7; // 调整星期一为周起始日

  // 增加空日期填补 grid
  for (let i = 0; i < startDayOfWeek; i++) {
    dates.push({ date: new Date(year, month, -i), isEmpty: true });
  }

  // 添加当前月份的日期
  for (let i = 1; i <= lastDayOfMonth.getDate(); i++) {
    const currentDate = new Date(year, month, i);
    if (currentDate <= MAX_DATE) {
      dates.push({ date: currentDate, isEmpty: false });
    }
  }

  // 如果需要，添加填补最后一周的空日期
  const remainingDays = 7 - (dates.length % 7);
  if (remainingDays < 7) {
    for (let i = 0; i < remainingDays; i++) {
      dates.push({
        date: new Date(year, month, lastDayOfMonth.getDate() + i + 1),
        isEmpty: true,
      });
    }
  }

  // 将日期按周分组
  const weeks: DateItem[][] = [];
  for (let i = 0; i < dates.length; i += 7) {
    weeks.push(dates.slice(i, i + 7));
  }

  return weeks;
};

const getSectionsForMonths = (
  year: number,
  month: number,
  numberOfMonths: number
): Section[] => {
  const sections: Section[] = [];
  for (let i = 0; i < numberOfMonths; i++) {
    const currentMonth = (month - i + 12) % 12;
    const currentYear = year - Math.floor((month - i) / 12);

    // 确保不加载超过当前月份的未来月份
    if (
      currentYear < today.getFullYear() ||
      (currentYear === today.getFullYear() && currentMonth <= today.getMonth())
    ) {
      const weeks = generateDatesForMonth(currentYear, currentMonth);
      sections.push({
        title: `${currentYear} / ${currentMonth + 1}`,
        data: weeks,
      });
    }
  }
  return sections.reverse(); // 倒序以确保最近月份显示在底部
};

export const SearchDatePickScreen = ({
  navigation,
  route,
}: MainNavProps<"SearchDatePickScreen">) => {
  const tid = route.params.tid;
  const [sections, setSections] = useState<Section[]>([]);
  const nowYear = today.getFullYear();
  const nowMonth = today.getMonth();
  const year = useRef(nowYear);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const sectionListRef = useRef<SectionList<any, any>>(null);
  const daysOfWeek = [
    Str_Monday,
    Str_Tuesday,
    Str_Wednesday,
    Str_Thursday,
    Str_Friday,
    Str_Saturday,
    Str_Sunday,
  ];

  useEffect(() => {
    const initialSections = getSectionsForMonths(nowYear, nowMonth, 12); // 初始化加载4个月的数据
    setSections(initialSections.reverse());
  }, []);

  function loadmore() {
    year.current -= 1;
    const initialSections = getSectionsForMonths(year.current, 11, 12); // 初始化加载4个月的数据
    const newSections = initialSections.reverse();
    setSections([...sections, ...newSections]);
  }

  const onClick = (item: DateItem) => {
    if (!item.isEmpty) {
      setSelectedDate(item.date);
      setTimeout(() => {
          navigation.navigate("QuickSearchMsgScreen", { tid, type: MessageType.LOCAL_TIME, date: item.date.getTime() })
      }, 300);
    }
  };

  const renderWeek = ({ item }: { item: DateItem[] }) => (
    <View style={styles.weekContainer}>
      {item.map((dateItem, index) => {
        const isSelected =
          selectedDate &&
          !dateItem.isEmpty &&
          dateItem.date.getTime() === selectedDate.getTime();
        const isToday =
          !dateItem.isEmpty &&
          dateItem.date.toDateString() === new Date().toDateString();

        return (
          <Pressable
            key={index}
            onPress={() => onClick(dateItem)}
            style={[
              styles.dateItem,
              isSelected && styles.selectedDateItem,
              isToday && !isSelected && styles.todayDateItem,
            ]}
          >
            {!dateItem.isEmpty ? (
              <Text
                style={[
                  styles.dateText,
                  isSelected && styles.selectedDateText,
                  isToday && !isSelected && styles.todayDateText,
                ]}
              >
                {dateItem.date.getDate()}
              </Text>
            ) : null}
          </Pressable>
        );
      })}
    </View>
  );

  const renderSectionHeader = ({
    section: { title },
  }: {
    section: Section;
  }) => (
    <View style={styles.sectionHeader}>
      <Text style={styles.sectionHeaderText}>{title}</Text>
      {Platform.OS === "web" && <View style={styles.sectionHeaderLine} />}
    </View>
  );

  return (
    <ScreenCloseModal title={Str_AccordingToDateFind}>
      <View style={styles.daysOfWeek}>
        {daysOfWeek.map((day) => (
          <View key={day} style={styles.dayOfWeekContainer}>
            <Text style={styles.dayOfWeekText}>{day}</Text>
          </View>
        ))}
      </View>
      <SectionList
        style={styles.sectionList}
        contentContainerStyle={styles.sectionListContent}
        ref={sectionListRef}
        sections={sections}
        renderItem={renderWeek}
        onEndReached={loadmore}
        onEndReachedThreshold={0.1}
        renderSectionHeader={renderSectionHeader}
        keyExtractor={(item, index) => index.toString()}
      />
    </ScreenCloseModal>
  );
};
const styles = StyleSheet.create({
  daysOfWeek: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingHorizontal: Platform.OS === "web" ? 24 : 10,
    paddingVertical: Platform.OS === "web" ? 16 : 12,
    backgroundColor: Platform.OS === "web" ? ColorF5F5F5 : "transparent",
    borderBottomWidth: Platform.OS === "web" ? 1 : 0,
    borderBottomColor: Platform.OS === "web" ? "#E0E0E0" : "transparent",
  },
  dayOfWeekContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  dayOfWeekText: {
    fontSize: Platform.OS === "web" ? 13 : 12,
    fontWeight: Platform.OS === "web" ? "600" : "bold",
    fontFamily: "",
    color: Platform.OS === "web" ? "#666666" : black90,
    textAlign: "center",
  },
  sectionList: {
    flex: 1,
    marginHorizontal: Platform.OS === "web" ? 20 : 10,
  },
  sectionListContent: {
    paddingBottom: Platform.OS === "web" ? 20 : 10,
  },
  sectionHeader: {
    paddingVertical: Platform.OS === "web" ? 20 : 15,
    paddingHorizontal: Platform.OS === "web" ? 4 : 0,
    backgroundColor: white,
    position: "relative",
    marginBottom: Platform.OS === "web" ? 8 : 0,
  },
  sectionHeaderText: {
    fontSize: Platform.OS === "web" ? 18 : 16,
    fontWeight: Platform.OS === "web" ? "600" : "normal",
    color: Platform.OS === "web" ? "#333333" : color_999999,
    marginStart: Platform.OS === "web" ? 4 : 20,
    marginBottom: Platform.OS === "web" ? 12 : 0,
  },
  sectionHeaderLine: {
    backgroundColor: ColorLine,
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: 0.5,
  },
  weekContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingHorizontal: Platform.OS === "web" ? 4 : 0,
    paddingVertical: Platform.OS === "web" ? 6 : 2,
    alignItems: "stretch",
  },
  dateItem: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    height: Platform.OS === "web" ? 48 : 40,
    minHeight: Platform.OS === "web" ? 48 : 40,
    borderRadius: Platform.OS === "web" ? 8 : 20,
    marginHorizontal: Platform.OS === "web" ? 3 : 2,
  },
  selectedDateItem: {
    backgroundColor: color_main,
  },
  todayDateItem: {
    backgroundColor: Platform.OS === "web" ? "#E8F5E9" : "transparent",
    borderWidth: Platform.OS === "web" ? 1.5 : 0,
    borderColor: Platform.OS === "web" ? color_main : "transparent",
  },
  dateText: {
    fontSize: Platform.OS === "web" ? 15 : 16,
    color: black90,
    fontWeight: Platform.OS === "web" ? "500" : "normal",
  },
  selectedDateText: {
    color: "white",
    fontWeight: Platform.OS === "web" ? "600" : "normal",
  },
  todayDateText: {
    color: Platform.OS === "web" ? color_main : black90,
    fontWeight: Platform.OS === "web" ? "600" : "normal",
  },
});
